/**
 * Sales Pressure Bar component with countdown timer and copy-to-clipboard functionality.
 */
class SalesPressureBar extends HTMLElement {
  constructor() {
    super();
    this.timerInterval = null;
    this.initialized = false;
  }

  connectedCallback() {
    this.init();
    setTimeout(() => this.init(), 100);
  }

  init() {
    if (this.initialized) return;
    
    if (this.querySelector('.sales-pressure-bar__container')) {
      this.initialized = true;
      this.startCountdown();
      this.setupCopyButton();
    }
  }

  disconnectedCallback() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  setupCopyButton() {
    const button = this.querySelector('.sales-pressure-bar__coupon');
    if (button && !button.hasAttribute('data-listener-attached')) {
      button.setAttribute('data-listener-attached', 'true');
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.copyCoupon();
      });
    }
  }

  get endDate() {
    const dateStr = this.getAttribute('end-date');
    if (!dateStr) return null;
    
    let date;
    if (dateStr.includes(' ') && !dateStr.includes('T')) {
      const isoStr = dateStr.replace(' ', 'T') + ':00';
      date = new Date(isoStr);
    } else {
      date = new Date(dateStr);
    }
    
    if (isNaN(date.getTime())) return null;
    return date;
  }

  startCountdown() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    const endDate = this.endDate;
    if (!endDate) return;

    if (endDate.getTime() - Date.now() <= 0) {
      this.hidden = true;
      return;
    }

    const updateTimer = () => {
      const now = new Date();
      const diff = endDate.getTime() - now.getTime();

      if (diff <= 0) {
        this.updateDisplay(0, 0, 0, 0);
        if (this.timerInterval) {
          clearInterval(this.timerInterval);
          this.timerInterval = null;
        }
        this.hidden = true;
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      this.updateDisplay(days, hours, minutes, seconds);
    };

    updateTimer();
    this.timerInterval = setInterval(updateTimer, 1000);
  }

  updateDisplay(days, hours, minutes, seconds) {
    const daysEl = this.querySelector('[ref="days"]');
    const hoursEl = this.querySelector('[ref="hours"]');
    const minutesEl = this.querySelector('[ref="minutes"]');
    const secondsEl = this.querySelector('[ref="seconds"]');
    
    if (daysEl) daysEl.textContent = String(days);
    if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
    if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
    if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
  }

  async copyCoupon() {
    const couponCode = this.getAttribute('coupon-code');
    if (!couponCode) return;

    try {
      await navigator.clipboard.writeText(couponCode);
      this.showCopiedMessage();
    } catch (error) {
      this.fallbackCopy(couponCode);
    }
  }

  fallbackCopy(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      this.showCopiedMessage();
    } catch (e) {
      // Silent fail
    }
    
    document.body.removeChild(textArea);
  }

  showCopiedMessage() {
    const msg = this.querySelector('[ref="copiedMessage"]');
    if (msg) {
      msg.classList.add('visible');
      setTimeout(() => msg.classList.remove('visible'), 2000);
    }
  }
}

if (!customElements.get('sales-pressure-bar')) {
  customElements.define('sales-pressure-bar', SalesPressureBar);
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('sales-pressure-bar').forEach(el => {
    if (el.init) el.init();
  });
});

if (document.readyState === 'complete' || document.readyState === 'interactive') {
  setTimeout(() => {
    document.querySelectorAll('sales-pressure-bar').forEach(el => {
      if (el.init) el.init();
    });
  }, 0);
}
